import type { CitationGraph, CitationNode } from '../../../src/contracts/types.ts';
import type {
  GraphGeometry,
  GraphLayout,
  GraphOrientation,
  NodeSize,
  NodeSizer,
  PlacedNode,
  VerticalGraphGeometry,
  VisibleNodes,
} from '../../../src/core/graphView.ts';
import { layoutGraph, layoutGraphVertical, routeEdge, selectVisibleNodes } from '../../../src/core/graphView.ts';
import { NARROW_LAYOUT_MAX_WIDTH, el, svg, clear } from './dom.ts';

/**
 * 引用グラフ。
 *
 * 方向が一目で分かることを最優先にする。
 * - 左: この論文が引用している論文（参考文献）
 * - 中央: この論文
 * - 右: この論文を引用している論文（被引用）
 *
 * 深さ2（引用の引用）が含まれる場合は、**同じ側のさらに外側の帯**へ置く。
 * 三列の意味（左＝過去、右＝未来）を壊さないため、深さ2も同じ向きにしか伸ばさない。
 * 深さは大きさ・破線・凡例で区別する。位置だけで区別すると、拡大するとどちらか分からなくなる。
 *
 * 器の幅が狭い（NARROW_LAYOUT_MAX_WIDTH 未満）ときは列→行に転置し、参考文献を上、被引用を下に積む。
 * 横配置は最小でも 780px を要り、390px の端末では横スクロールしないと中心が見えなかった。
 * 縦配置の列数と列幅は器の幅から決め、ノード幅の下限（150px）を割る幅のときだけはみ出す。
 *
 * 力学モデルの塊にすると方向が読めなくなるため、列配置を基本にし、
 * 乱数を使わないので再描画しても同じ絵になる（座標と辺の経路は src/core/graphView.ts が決める）。
 * ノードが多い場合は列を増やし、それでも収まらない分は引用数の多い順に絞る。
 */

export interface GraphFilters {
  direction: 'all' | 'references' | 'cited_by';
  internalOnly: boolean;
  /** 表示する深さ。省略時は取得できた深さをすべて表示する。 */
  depth?: 1 | 2;
}

export interface GraphOptions {
  onSelect: (node: CitationNode & { slug?: string }) => void;
  onFilterChange?: (filters: GraphFilters) => void;
  filters: GraphFilters;
  /**
   * 描画先の幅（px）。取り付け前は幅が測れないので呼び出し側が渡す。
   * 省略時は文書の幅で始め、取り付け後に ResizeObserver で器の幅へ追従する。
   */
  containerWidth?: number;
}

type Placed = PlacedNode;

/** 列配置の寸法。計算そのものは src/core/graphView.ts にあり、乱数も画面幅も使わない。 */
const GEOMETRY: GraphGeometry = {
  rowHeight: 82,
  maxRows: 9,
  columnWidth: 240,
  centerBand: 300,
  edgeMargin: 120,
  minRows: 5,
  minHeight: 480,
};

/** 縦配置でのノード幅の下限。これより縮めると題名が読めない。 */
const MIN_NODE_WIDTH = 150;
/** 縦配置で隣の列との間に空ける幅。辺の折れ線はこの隙間を縦に通る。 */
const COLUMN_GUTTER = 10;

/** 縦配置の寸法。width は描画時に器の幅で埋める。 */
const VERTICAL_GEOMETRY: Omit<VerticalGraphGeometry, 'width'> = {
  minColumnWidth: MIN_NODE_WIDTH + COLUMN_GUTTER,
  maxColumnWidth: GEOMETRY.columnWidth,
  rowHeight: GEOMETRY.rowHeight,
  bandGap: 40,
  centerBand: 120,
  edgeMargin: 24,
  minHeight: 320,
};

/** 縦配置で、器の幅がこれだけ変わったら引き直す。1px ごとに引き直すと入力の追従が重くなる。 */
const REDRAW_WIDTH_STEP = 16;

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max)}…`;
}

function nodeSize(isCenter: boolean, depth = 1): NodeSize {
  if (isCenter) return { width: 220, height: 86 };
  return depth >= 2 ? { width: 160, height: 60 } : { width: 190, height: 70 };
}

/** ノードの大きさ。縦配置では列幅に合わせて縮む（中心は縮めない）。 */
function sizerFor(layout: GraphLayout): NodeSizer {
  const maxWidth = layout.orientation === 'vertical' ? layout.columnWidth - COLUMN_GUTTER : Number.POSITIVE_INFINITY;
  return (node) => {
    const base = nodeSize(node.side === 'center', node.depth);
    if (node.side === 'center') return base;
    return { width: Math.min(base.width, maxWidth), height: base.height };
  };
}

/** 1行に載せる文字数。基準の幅（190px で 23 文字）から比例で縮める。 */
function charBudget(size: NodeSize, isCenter: boolean, depth: number): number {
  const base = nodeSize(isCenter, depth);
  const baseChars = isCenter ? 28 : depth >= 2 ? 19 : 23;
  return Math.max(8, Math.floor((baseChars * size.width) / base.width));
}

function labelLines(text: string, max: number): string[] {
  const characters = Array.from(text.trim());
  if (characters.length <= max) return [text.trim()];
  let cut = characters.slice(0, max + 1).lastIndexOf(' ');
  if (cut < Math.floor(max / 2)) cut = max;
  const first = characters.slice(0, cut).join('').trim();
  const rest = characters.slice(cut).join('').trim();
  return [first, truncate(rest, max)];
}

interface Layout extends GraphLayout {
  edges: Array<{ from: Placed; to: Placed; direction: 'references' | 'cited_by'; depth: number }>;
}

function buildLayout(
  graph: CitationGraph,
  visible: VisibleNodes,
  orientation: GraphOrientation,
  availableWidth: number,
): Layout {
  const byKey = new Map(graph.nodes.map((n) => [n.key, n]));
  // どのノードがどちら側の何段目で、どこに置くかは src/core/graphView.ts が決める。
  // ここは色と SVG だけを足す（並びと座標の決定論は core 側のテストで確かめている）。
  const args = { centerKey: graph.centerKey, center: byKey.get(graph.centerKey), visible };
  const layout =
    orientation === 'vertical'
      ? layoutGraphVertical({ ...args, geometry: { ...VERTICAL_GEOMETRY, width: Math.max(1, Math.floor(availableWidth)) } })
      : layoutGraph({ ...args, geometry: GEOMETRY });

  const placedByKey = new Map<string, Placed>();
  for (const item of layout.placed) placedByKey.set(item.node.key, item);

  // 辺は保存されている向きのまま引く。深さ2でも「引用する側 → 引用される側」は変わらない。
  const edges: Layout['edges'] = [];
  for (const edge of graph.edges) {
    const from = placedByKey.get(edge.fromKey);
    const to = placedByKey.get(edge.toKey);
    if (!from || !to || from === to) continue;
    edges.push({ from, to, direction: edge.direction, depth: Math.max(from.depth, to.depth) });
  }

  return { ...layout, edges };
}

/** 関係の言い方は深さで変える。深さ2を「この論文が引用している」と書くと嘘になる。 */
function relationLabel(item: Placed): string {
  const isDepth2 = item.depth >= 2;
  if (item.side === 'center') return 'この論文';
  if (item.side === 'left') return isDepth2 ? 'この論文の参考文献が引用している（深さ2）' : 'この論文が引用している';
  return isDepth2 ? 'この論文を引用した論文を、さらに引用している（深さ2）' : 'この論文を引用している';
}

export function renderCitationGraph(graph: CitationGraph, options: GraphOptions): HTMLElement {
  const container = el('section', { class: 'graph', 'aria-label': '引用グラフ' });
  // 向きの説明は配置（横／縦）で変わるので、描くたびに書き換える。
  const orientationHint = el('p', {});
  container.appendChild(
    el(
      'div',
      { class: 'graph__head' },
      el('span', { class: 'visual-summary__eyebrow' }, 'CITATION MAP'),
      el('h3', {}, 'この研究の前後をたどる'),
      orientationHint,
    ),
  );

  // --- ツールバー ---
  const toolbar = el('div', { class: 'graph__toolbar' });
  const directionGroup = el('div', { class: 'segmented', role: 'group', 'aria-label': '引用の向き' });
  for (const direction of [
    { value: 'all' as const, label: 'すべて' },
    { value: 'references' as const, label: '参考にした論文' },
    { value: 'cited_by' as const, label: '後続の論文' },
  ]) {
    directionGroup.appendChild(
      el(
        'button',
        {
          type: 'button',
          class: 'segmented__item',
          'aria-pressed': String(options.filters.direction === direction.value),
          onclick: () => options.onFilterChange?.({ ...options.filters, direction: direction.value }),
        },
        direction.label,
      ),
    );
  }
  toolbar.appendChild(directionGroup);

  const internalToggle = el('label', { class: 'chip' });
  internalToggle.appendChild(
    el('input', {
      type: 'checkbox',
      checked: options.filters.internalOnly,
      onchange: (event: Event) =>
        options.onFilterChange?.({ ...options.filters, internalOnly: (event.target as HTMLInputElement).checked }),
    }),
  );
  internalToggle.appendChild(document.createTextNode(' PaperStock に解説がある論文のみ'));
  toolbar.appendChild(internalToggle);

  // 深さ2の辺があるときだけ深さの切り替えを出す。無いときに出すと
  // 「切っているから見えない」のか「取得していない」のか分からなくなる。
  const hasDepth2 = graph.edges.some((edge) => (edge.depth ?? 1) >= 2);
  const shownDepth: 1 | 2 = options.filters.depth ?? 2;
  if (hasDepth2) {
    const depthToggle = el('label', { class: 'chip' });
    depthToggle.appendChild(
      el('input', {
        type: 'checkbox',
        checked: shownDepth >= 2,
        onchange: (event: Event) =>
          options.onFilterChange?.({
            ...options.filters,
            depth: (event.target as HTMLInputElement).checked ? 2 : 1,
          }),
      }),
    );
    depthToggle.appendChild(document.createTextNode(' 引用の引用（深さ2）まで表示'));
    toolbar.appendChild(depthToggle);
  }

  container.appendChild(toolbar);

  if (graph.nodes.length <= 1 || graph.edges.length === 0) {
    orientationHint.textContent = '参考にした論文と、この研究を参考にした後続論文を、この論文を中心に並べます。';
    container.appendChild(
      el('p', { class: 'state' }, '引用関係を表示できません。このグラフには参考文献・被引用の辺が含まれていません。'),
    );
    return container;
  }

  // 表示するノードは配置の向きに関係なく同じ。向きを変えても一覧と件数は変わらない。
  const visible = selectVisibleNodes(graph, {
    maxDepth: shownDepth,
    maxPerSide: graph.nodes.length,
    maxDepth2PerSide: graph.nodes.length,
  });

  const scroller = el('div', {
    class: 'graph__scroller',
    tabindex: '0',
    role: 'region',
    'aria-label': '引用グラフ',
  });
  container.appendChild(scroller);

  // 詳細はグラフ下の別パネルではなく、選んだノードの近くに1つだけ出す。
  // ノードごとに詳細ポップオーバーの DOM を複製しない。
  const detail = el('div', {
    id: 'citation-node-detail',
    class: 'graph__detail graph__popover',
    role: 'dialog',
    'aria-label': '引用論文の詳細',
    tabindex: '-1',
    hidden: true,
    style:
      'position:absolute;z-index:5;box-sizing:border-box;width:min(22rem,calc(100% - 1rem));max-height:min(24rem,calc(100% - 1rem));overflow:auto;border:1px solid var(--border-strong);border-radius:var(--radius);box-shadow:0 12px 32px rgba(0,0,0,.2)',
  });
  container.appendChild(detail);
  const citationList = el('ol', { style: 'margin:.75rem 0 0;padding-left:1.5rem;display:grid;gap:.65rem' });

  // 引き直しのたびに差し替わる SVG の層。ハイライトはここを見る。
  let edgeLayer: SVGElement | null = null;
  let nodeLayer: SVGElement | null = null;
  let activeGroup: SVGElement | null = null;
  let pinned = false;
  let closeTimer: number | undefined;

  const clearHighlight = (): void => {
    edgeLayer?.querySelectorAll('.graph-edge').forEach((edge) => edge.classList.remove('graph-edge--active', 'graph-edge--dimmed'));
    nodeLayer?.querySelectorAll('.graph-node').forEach((node) => node.classList.remove('graph-node--dimmed'));
  };
  const highlight = (key: string): void => {
    const connected = new Set<string>([key]);
    edgeLayer?.querySelectorAll<SVGElement>('.graph-edge').forEach((edge) => {
      const from = edge.dataset['from'];
      const to = edge.dataset['to'];
      const active = from === key || to === key;
      edge.classList.toggle('graph-edge--active', active);
      edge.classList.toggle('graph-edge--dimmed', !active);
      if (active && from) connected.add(from);
      if (active && to) connected.add(to);
    });
    nodeLayer?.querySelectorAll<SVGElement>('.graph-node').forEach((node) => {
      node.classList.toggle('graph-node--dimmed', !connected.has(node.dataset['key'] ?? ''));
    });
  };

  const cancelClose = (): void => {
    if (closeTimer !== undefined) window.clearTimeout(closeTimer);
    closeTimer = undefined;
  };
  const positionDetail = (anchor: SVGElement): void => {
    const bounds = container.getBoundingClientRect();
    const anchorBounds = anchor.getBoundingClientRect();
    const edge = 8;
    const gap = 10;
    const width = detail.offsetWidth;
    const height = detail.offsetHeight;
    let left = anchorBounds.right - bounds.left + gap;
    if (left + width + edge > bounds.width) left = anchorBounds.left - bounds.left - width - gap;
    left = Math.max(edge, Math.min(left, bounds.width - width - edge));
    const centeredTop = anchorBounds.top - bounds.top + anchorBounds.height / 2 - height / 2;
    const top = Math.max(edge, Math.min(centeredTop, bounds.height - height - edge));
    detail.style.left = `${left}px`;
    detail.style.top = `${top}px`;
    detail.style.visibility = '';
  };
  const onEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || detail.hidden) return;
    event.preventDefault();
    closeDetail(true);
  };
  const closeDetail = (restoreFocus = false): void => {
    cancelClose();
    document.removeEventListener('keydown', onEscape);
    detail.hidden = true;
    pinned = false;
    clearHighlight();
    const previous = activeGroup;
    previous?.setAttribute('aria-expanded', 'false');
    activeGroup = null;
    if (restoreFocus) (previous as unknown as HTMLElement | null)?.focus();
  };
  const scheduleClose = (): void => {
    cancelClose();
    if (pinned) return;
    closeTimer = window.setTimeout(() => {
      if (!detail.matches(':hover') && !detail.contains(document.activeElement)) closeDetail();
    }, 120);
  };

  detail.addEventListener('mouseenter', cancelClose);
  detail.addEventListener('mouseleave', scheduleClose);
  detail.addEventListener('focusin', cancelClose);
  detail.addEventListener('focusout', scheduleClose);
  scroller.addEventListener('scroll', () => {
    if (!detail.hidden && activeGroup) positionDetail(activeGroup);
  });

  const showDetail = (item: Placed, group: SVGElement, pin: boolean, moveFocus = false): void => {
    const isCenter = item.side === 'center';
    if (pinned && !pin && activeGroup !== group) return;
    if (pin && pinned && activeGroup === group) {
      closeDetail(true);
      return;
    }
    cancelClose();
    activeGroup?.setAttribute('aria-expanded', 'false');
    activeGroup = group;
    pinned = pin;
    highlight(item.node.key);
    group.setAttribute('aria-expanded', 'true');
    clear(detail);
    const closeButton = el(
      'button',
      {
        class: 'icon-button',
        type: 'button',
        'aria-label': '詳細を閉じる',
        onclick: () => closeDetail(true),
      },
      '×',
    );
    detail.appendChild(
      el(
        'div',
        { style: 'display:flex;align-items:start;gap:.75rem' },
        el('strong', { style: 'flex:1' }, item.node.title),
        closeButton,
      ),
    );
    const authors = item.node.authors.slice(0, 3);
    detail.appendChild(
      el(
        'div',
        { class: 'muted', style: 'margin-top:.35rem' },
        [
          authors.length > 0
            ? `${authors.join(', ')}${item.node.authors.length > authors.length ? ` ほか${item.node.authors.length - authors.length}名` : ''}`
            : null,
          item.node.year ? `${item.node.year}年` : null,
          item.node.venue,
          item.node.citationCount !== undefined ? `引用 ${item.node.citationCount}` : null,
        ]
          .filter(Boolean)
          .join('｜'),
      ),
    );
    detail.appendChild(el('p', { class: 'muted', style: 'margin:.45rem 0 0' }, relationLabel(item)));
    const actions = el('div', { class: 'row', style: 'margin-top:0.5rem' });
    if (item.node.internal && !isCenter) {
      actions.appendChild(
        el(
          'button',
          { class: 'button button--primary', type: 'button', onclick: () => options.onSelect(item.node) },
          'PaperStock の解説を読む',
        ),
      );
    } else if (!isCenter) {
      actions.appendChild(el('span', { class: 'chip' }, 'PaperStock 未収載（外部論文）'));
    }
    if (item.node.url) {
      actions.appendChild(
        el('a', { class: 'button', href: item.node.url, target: '_blank', rel: 'noopener noreferrer' }, '原論文を開く'),
      );
    }
    detail.appendChild(actions);
    detail.hidden = false;
    detail.style.visibility = 'hidden';
    positionDetail(group);
    document.removeEventListener('keydown', onEscape);
    document.addEventListener('keydown', onEscape);
    if (moveFocus) detail.focus();
  };

  const fillCitationList = (layout: Layout): void => {
    for (const item of layout.placed) {
      if (item.side === 'center') continue;
      const relation = relationLabel(item);
      const title = item.node.url
        ? el('a', { href: item.node.url, target: '_blank', rel: 'noopener noreferrer' }, item.node.title)
        : el('span', {}, item.node.title);
      citationList.appendChild(
        el(
          'li',
          {},
          title,
          el(
            'span',
            { class: 'muted', style: 'display:block;font-size:.78rem' },
            [item.node.authors.slice(0, 3).join(', '), item.node.year, item.node.venue, relation].filter(Boolean).join('｜'),
          ),
          item.node.internal
            ? el(
                'button',
                {
                  class: 'button',
                  type: 'button',
                  style: 'margin-top:.3rem;padding:.25rem .55rem',
                  onclick: () => options.onSelect(item.node),
                },
                'PaperStock の解説を読む',
              )
            : null,
        ),
      );
    }
  };

  const buildCanvas = (layout: Layout): SVGElement => {
    const sizeOf = sizerFor(layout);
    const vertical = layout.orientation === 'vertical';
    const canvas = svg('svg', {
      class: `graph__canvas graph__canvas--${layout.orientation}`,
      width: layout.width,
      height: layout.height,
      viewBox: `0 0 ${layout.width} ${layout.height}`,
      role: 'group',
      'aria-label':
        `引用グラフ。この論文が引用している論文 ${visible.referenceCount} 件、この論文を引用している論文 ${visible.citedByCount} 件。` +
        (shownDepth >= 2 && (visible.depth2ReferenceCount > 0 || visible.depth2CitedByCount > 0)
          ? `さらにその参考文献 ${visible.depth2ReferenceCount} 件、さらにその被引用 ${visible.depth2CitedByCount} 件（深さ2）。`
          : '') +
        (vertical ? '上が参考文献、下が被引用です。' : '左が参考文献、右が被引用です。'),
    });

    const defs = svg('defs');
    for (const id of ['ps-arrow-ref', 'ps-arrow-cited'] as const) {
      const marker = svg('marker', {
        id,
        viewBox: '0 0 10 10',
        refX: '9',
        refY: '5',
        markerWidth: '5',
        markerHeight: '5',
        orient: 'auto',
      });
      marker.appendChild(svg('path', { d: 'M 0 0 L 10 5 L 0 10 z', fill: 'var(--border-strong)' }));
      defs.appendChild(marker);
    }
    canvas.appendChild(defs);

    const viewport = svg('g');
    canvas.appendChild(viewport);
    const backdropLayer = svg('g', { class: 'graph__zones', 'aria-hidden': 'true' });
    const edges = svg('g');
    const nodes = svg('g');
    // ハイライトは最新の層を見る。古い SVG は差し替えで捨てる。
    edgeLayer = edges;
    nodeLayer = nodes;
    viewport.appendChild(backdropLayer);
    viewport.appendChild(edges);
    viewport.appendChild(nodes);

    const center = layout.placed.find((item) => item.side === 'center');
    const centerX = center?.x ?? layout.width / 2;
    const centerY = center?.y ?? layout.height / 2;
    if (vertical) {
      // 縦配置の帯: 上＝参考文献、中央、下＝被引用。
      const half = VERTICAL_GEOMETRY.centerBand / 2 - 4;
      backdropLayer.appendChild(svg('rect', { class: 'graph__zone', x: 12, y: 12, width: Math.max(0, layout.width - 24), height: Math.max(0, centerY - half - 12), rx: 18 }));
      backdropLayer.appendChild(svg('rect', { class: 'graph__zone graph__zone--center', x: 12, y: centerY - half, width: Math.max(0, layout.width - 24), height: half * 2, rx: 18 }));
      backdropLayer.appendChild(svg('rect', { class: 'graph__zone', x: 12, y: centerY + half, width: Math.max(0, layout.width - 24), height: Math.max(0, layout.height - centerY - half - 12), rx: 18 }));
    } else {
      backdropLayer.appendChild(svg('rect', { class: 'graph__zone', x: 12, y: 12, width: Math.max(0, centerX - 110), height: Math.max(0, layout.height - 28), rx: 18 }));
      backdropLayer.appendChild(svg('rect', { class: 'graph__zone graph__zone--center', x: centerX - 78, y: 12, width: 156, height: Math.max(0, layout.height - 28), rx: 18 }));
      backdropLayer.appendChild(svg('rect', { class: 'graph__zone', x: centerX + 110, y: 12, width: Math.max(0, layout.width - centerX - 122), height: Math.max(0, layout.height - 28), rx: 18 }));
    }

    for (const edge of layout.edges) {
      const isReference = edge.direction === 'references';
      const isDepth2 = edge.depth >= 2;
      // 直線ではなく、帯と帯の隙間を通る折れ線。外側の列からの辺が内側のカードを貫かないようにする。
      const points = routeEdge(layout, edge.from, edge.to, sizeOf, 3);
      edges.appendChild(
        svg('polyline', {
          points: points.map((point) => `${point.x},${point.y}`).join(' '),
          class: 'graph-edge',
          'data-from': edge.from.node.key,
          'data-to': edge.to.node.key,
          fill: 'none',
          stroke: 'var(--border-strong)',
          'stroke-width': edge.to.node.internal || edge.from.node.internal ? '1.6' : '1',
          'stroke-linejoin': 'round',
          // 深さ2は破線かつ薄くする。中心に直接つながる関係と見分けが付かないと、
          // 「この論文が引用している」と読み違える。
          'stroke-opacity': isDepth2 ? '0.2' : '0.34',
          ...(isDepth2 ? { 'stroke-dasharray': '4 3' } : {}),
          'marker-end': `url(#${isReference ? 'ps-arrow-ref' : 'ps-arrow-cited'})`,
        }),
      );
    }

    for (const item of layout.placed) {
      const isCenter = item.side === 'center';
      const isDepth2 = item.depth >= 2;
      const size = sizeOf(item);
      const relation = relationLabel(item);
      const group = svg('g', {
        class: `graph-node${isCenter ? ' graph-node--center' : ''}${item.node.internal ? ' graph-node--internal' : ''}${isDepth2 ? ' graph-node--depth2' : ''}`,
        'data-key': item.node.key,
        tabindex: '0',
        role: 'button',
        'aria-haspopup': 'dialog',
        'aria-controls': 'citation-node-detail',
        'aria-expanded': 'false',
        'aria-label': [
          item.node.title,
          item.node.authors[0],
          item.node.year ? `${item.node.year}年` : null,
          relation,
          item.node.internal ? 'PaperStock に解説あり' : '外部論文',
        ].filter(Boolean).join('｜'),
      });
      group.appendChild(svg('title', {}, [item.node.title, item.node.authors.join(', ')].filter(Boolean).join('｜')));

      group.appendChild(svg('rect', {
        class: 'graph-node__hit',
        x: item.x - size.width / 2,
        y: item.y - size.height / 2,
        width: size.width,
        height: size.height,
        rx: 13,
        fill: 'transparent',
      }));

      group.appendChild(
        svg('rect', {
          class: 'graph-node__card',
          x: item.x - size.width / 2,
          y: item.y - size.height / 2,
          width: size.width,
          height: size.height,
          rx: 13,
          'stroke-width': isCenter ? '2.5' : '1.4',
          ...(isDepth2 ? { 'stroke-dasharray': '4 3' } : {}),
        }),
      );

      if (isCenter) {
        group.appendChild(svg('text', { class: 'graph-node__eyebrow', x: item.x, y: item.y - 27, 'text-anchor': 'middle' }, 'THIS PAPER'));
      }
      const title = svg('text', {
        class: 'graph-node__title',
        x: item.x,
        y: item.y + (isCenter ? -7 : isDepth2 ? -5 : -8),
        'text-anchor': 'middle',
      });
      const budget = charBudget(size, isCenter, item.depth);
      for (const [index, line] of labelLines(item.node.title, budget).entries()) {
        title.appendChild(svg('tspan', { x: item.x, dy: index === 0 ? 0 : 14 }, line));
      }
      group.appendChild(title);
      const meta = [item.node.year, item.node.authors[0]].filter(Boolean).join(' · ');
      if (meta) {
        group.appendChild(
          svg(
            'text',
            {
              class: 'graph-node__meta',
              x: item.x,
              y: item.y + size.height / 2 - 8,
              'text-anchor': 'middle',
            },
            truncate(meta, Math.max(10, budget + (isDepth2 ? 4 : 7))),
          ),
        );
      }

      group.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
        showDetail(item, group, true);
      });
      group.addEventListener('mouseenter', () => showDetail(item, group, false));
      group.addEventListener('mouseleave', scheduleClose);
      group.addEventListener('focus', () => showDetail(item, group, false));
      group.addEventListener('blur', scheduleClose);
      group.addEventListener('keydown', (event) => {
        const key = (event as KeyboardEvent).key;
        if (key === 'Enter' || key === ' ') {
          event.preventDefault();
          showDetail(item, group, true, true);
        }
      });
      nodes.appendChild(group);
    }

    return canvas;
  };

  let drawn: { orientation: GraphOrientation; width: number } | null = null;
  const draw = (availableWidth: number): void => {
    const orientation: GraphOrientation = availableWidth < NARROW_LAYOUT_MAX_WIDTH ? 'vertical' : 'horizontal';
    // 横配置は器の幅に依存しない。縦配置は幅が一定以上変わったときだけ引き直す。
    if (
      drawn &&
      drawn.orientation === orientation &&
      (orientation === 'horizontal' || Math.abs(drawn.width - availableWidth) < REDRAW_WIDTH_STEP)
    ) {
      return;
    }
    drawn = { orientation, width: availableWidth };
    closeDetail();
    const layout = buildLayout(graph, visible, orientation, availableWidth);
    const canvas = buildCanvas(layout);
    clear(scroller);
    scroller.appendChild(canvas);
    const vertical = orientation === 'vertical';
    orientationHint.textContent = vertical
      ? '上はこの研究が参考にした論文、下はこの研究を参考にした後続論文です。カードを選ぶと著者と関係を確認できます。'
      : '左はこの研究が参考にした論文、右はこの研究を参考にした後続論文です。点を選ぶと著者と関係を確認できます。';
    scroller.setAttribute(
      'aria-label',
      vertical ? '引用グラフ。上が参考にした論文、下が後続の論文です' : '引用グラフ。横にスクロールして前後の論文を確認できます',
    );
    if (!vertical) {
      const centerX = layout.placed.find((item) => item.side === 'center')?.x ?? layout.width / 2;
      window.requestAnimationFrame(() => {
        const maxScrollLeft = Math.max(0, layout.width - scroller.clientWidth);
        scroller.scrollLeft = Math.max(0, Math.min(maxScrollLeft, centerX - scroller.clientWidth / 2));
      });
    }
    if (citationList.childElementCount === 0) fillCitationList(layout);
  };

  // 取り付け前は器の幅が測れないので、呼び出し側の値か文書の幅で始め、取り付け後に器の幅へ追従する。
  draw(options.containerWidth ?? document.documentElement.clientWidth);
  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width > 0) draw(width);
    });
    observer.observe(container);
  }

  container.appendChild(
    el(
      'details',
      { style: 'border-top:1px solid var(--border);padding:var(--space-3);font-size:.85rem' },
      el('summary', { style: 'cursor:pointer;font-weight:600' }, `表示中の論文を一覧で確認（${citationList.childElementCount}件）`),
      citationList,
    ),
  );

  const dropped = visible.dropped;
  container.appendChild(
    el(
      'div',
      { class: 'graph__legend' },
      el('span', {}, el('span', { class: 'graph__swatch', style: 'background:var(--accent)' }), 'PaperStock に解説あり'),
      el(
        'span',
        {},
        el('span', { class: 'graph__swatch', style: 'background:var(--surface-1);border:1px solid var(--border-strong)' }),
        '外部論文',
      ),
      hasDepth2
        ? el(
            'span',
            {},
            el('span', {
              class: 'graph__swatch',
              style: 'background:var(--surface-1);border:1px dashed var(--border-strong);opacity:0.75',
            }),
            '深さ2（引用の引用。破線）',
          )
        : null,
      dropped > 0
        ? el('span', { class: 'chip chip--warn' }, `表示上限のため ${dropped} 件を省略（引用数の多い順に表示）`)
        : null,
      graph.truncated.references || graph.truncated.citedBy
        ? el('span', { class: 'chip chip--warn' }, '提供元からの取得も一部にとどまっています')
        : null,
      // 深さ2は「無い」のか「辿っていない」のかを必ず言う。黙って浅いグラフを見せない。
      graph.truncated.depth2
        ? el('span', { class: 'chip chip--warn' }, '深さ2（引用の引用）は一部しか取得できていません')
        : null,
      !hasDepth2 && (graph.maxDepth ?? 1) < 2
        ? el('span', { class: 'chip' }, 'このグラフは深さ1（この論文の直接の引用関係）までです')
        : null,
    ),
  );
  for (const note of graph.depthNotes ?? []) {
    container.appendChild(el('p', { class: 'muted', style: 'margin:0.25rem 0 0' }, `深さ2について: ${note}`));
  }

  return container;
}
